import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type { JobOrder, CopyOrderParameters } from '@/lib/models/jobOrder';
import type { JobOrderJobActivity } from '@/lib/models/jobOrderJobActivity';
import type { JobOrderAllowedWorker } from '@/lib/models/jobOrderAllowedWorker';
import type { JobOrderAllowedGroup } from '@/lib/models/jobOrderAllowedGroup';
import type { JobOrderManager } from '@/lib/models/jobOrderManager';
import type { JobOrderRequirement } from '@/lib/models/jobOrderRequirement';
import type { OrderRequirementAnalysis } from '@/lib/models/orderRequirementAnalysis';
import type {
	HoursPerActivityPerUserChartData,
	HoursPerActivityChartData,
} from '@/lib/models/chartData';
import type { RequirementAutocomplete } from '@/lib/models/requirementAutocomplete';
import type { ODataResponse } from '@/lib/models/common';

/**
 * Interfaccia per gli endpoints Order
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 */
export interface OrderEndpoints {
	/**
	 * Recupera lista paginata di job orders con query OData
	 * @param query - Query string OData
	 * @returns Promise con FetchResponse contenente ODataResponse<JobOrder>
	 */
	getOrders(query: string): Promise<FetchResponse<ODataResponse<JobOrder>>>;

	/**
	 * Recupera un singolo job order per ID
	 * @param id - ID del job order
	 * @returns Promise con FetchResponse contenente JobOrder
	 */
	getOrder(id: number): Promise<FetchResponse<JobOrder>>;

	/**
	 * Aggiorna un job order esistente
	 * @param id - ID del job order da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con FetchResponse contenente JobOrder aggiornato
	 */
	updateOrder(id: number, payload: JobOrder): Promise<FetchResponse<JobOrder>>;

	/**
	 * Crea un nuovo job order
	 * @param payload - Dati del nuovo job order
	 * @returns Promise con FetchResponse contenente JobOrder creato
	 */
	postOrder(payload: JobOrder): Promise<FetchResponse<JobOrder>>;

	/**
	 * Elimina un job order
	 * @param id - ID del job order da eliminare
	 * @returns Promise con FetchResponse contenente void
	 */
	deleteOrder(id: number): Promise<FetchResponse<void>>;

	/**
	 * Recupera l'analisi dei requisiti per un job order
	 * @param id - ID del job order
	 * @returns Promise con FetchResponse contenente OrderRequirementAnalysis
	 */
	getRequirementAnalysis(id: number): Promise<FetchResponse<OrderRequirementAnalysis>>;

	/**
	 * Copia un job order esistente
	 * @param id - ID del job order da copiare
	 * @param payload - Parametri per la copia (CopyOrderParameters)
	 * @returns Promise con FetchResponse contenente JobOrder copiato
	 */
	copy(id: number, payload: CopyOrderParameters): Promise<FetchResponse<JobOrder>>;

	/**
	 * Recupera le attività consentite per un job order
	 * @param orderId - ID del job order
	 * @returns Promise con FetchResponse contenente ODataResponse<JobOrderJobActivity>
	 */
	getAllowedActivities(orderId: number): Promise<FetchResponse<ODataResponse<JobOrderJobActivity>>>;

	/**
	 * Recupera i lavoratori autorizzati per un job order
	 * @param orderId - ID del job order
	 * @returns Promise con FetchResponse contenente ODataResponse<JobOrderAllowedWorker>
	 */
	getAllowedWorkers(orderId: number): Promise<FetchResponse<ODataResponse<JobOrderAllowedWorker>>>;

	/**
	 * Recupera i gruppi autorizzati per un job order
	 * @param orderId - ID del job order
	 * @returns Promise con FetchResponse contenente ODataResponse<JobOrderAllowedGroup>
	 */
	getAllowedGroups(orderId: number): Promise<FetchResponse<ODataResponse<JobOrderAllowedGroup>>>;

	/**
	 * Recupera i manager per un job order
	 * @param orderId - ID del job order
	 * @returns Promise con FetchResponse contenente ODataResponse<JobOrderManager>
	 */
	getAllowedManagers(orderId: number): Promise<FetchResponse<ODataResponse<JobOrderManager>>>;

	/**
	 * Recupera i requisiti per un job order
	 * @param orderId - ID del job order
	 * @returns Promise con FetchResponse contenente ODataResponse<JobOrderRequirement>
	 */
	getOrderRequirements(orderId: number): Promise<FetchResponse<ODataResponse<JobOrderRequirement>>>;

	/**
	 * Recupera dati del grafico ore per attività per utente
	 * @param orderId - ID del job order
	 * @param approved - Se considerare solo ore approvate
	 * @returns Promise con FetchResponse contenente HoursPerActivityPerUserChartData
	 */
	getHoursPerActivityPerUserChartData(
		orderId: number,
		approved: boolean,
	): Promise<FetchResponse<HoursPerActivityPerUserChartData>>;

	/**
	 * Recupera dati del grafico ore per attività
	 * @param orderId - ID del job order
	 * @param approved - Se considerare solo ore approvate
	 * @returns Promise con FetchResponse contenente HoursPerActivityChartData
	 */
	getHoursPerActivityChartData(
		orderId: number,
		approved: boolean,
	): Promise<FetchResponse<HoursPerActivityChartData>>;

	/**
	 * Recupera requisiti filtrati per job order
	 * @param orderId - ID del job order (0 se nullo o vuoto)
	 * @param searchString - Testo di ricerca
	 * @returns Promise con FetchResponse contenente array di RequirementAutocomplete
	 */
	getRequirements(
		orderId: number | null,
		searchString: string,
	): Promise<FetchResponse<RequirementAutocomplete[]>>;

	/**
	 * Genera Excel per job orders
	 * @param query - Query string per il filtro
	 * @returns Promise con FetchResponse contenente Blob Excel
	 */
	generateExcel(query: string): Promise<FetchResponse<Blob>>;

	/**
	 * Upload di job orders da file
	 * @param form - FormData contenente il file
	 * @returns Promise con FetchResponse contenente Blob di risposta
	 */
	uploadOrders(form: FormData): Promise<FetchResponse<Blob>>;
}

/**
 * Implementazione degli endpoints per Order
 * Mantiene esattamente gli stessi nomi dei metodi e URL del JavaScript originale
 */
const orderEndpoints: OrderEndpoints = {
	getOrders(query: string): Promise<FetchResponse<ODataResponse<JobOrder>>> {
		return api.get(`/odata/v1/JobOrders${query}`);
	},

	getOrder(id: number): Promise<FetchResponse<JobOrder>> {
		return api.get(`/odata/v1/JobOrders/${id}`);
	},

	updateOrder(id: number, payload: JobOrder): Promise<FetchResponse<JobOrder>> {
		return api.put(`/api/v1/JobOrders/${id}`, payload);
	},

	postOrder(payload: JobOrder): Promise<FetchResponse<JobOrder>> {
		return api.post('/api/v1/JobOrders', payload);
	},

	deleteOrder(id: number): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/JobOrders/${id}`);
	},

	getRequirementAnalysis(id: number): Promise<FetchResponse<OrderRequirementAnalysis>> {
		return api.get(`/api/v1/JobOrders/${id}/actions/getRequirementAnalysis`);
	},

	copy(id: number, payload: CopyOrderParameters): Promise<FetchResponse<JobOrder>> {
		return api.post(`/api/v1/JobOrders/${id}/actions/copy`, payload);
	},

	getAllowedActivities(
		orderId: number,
	): Promise<FetchResponse<ODataResponse<JobOrderJobActivity>>> {
		return api.get(
			`/odata/v1/JobOrderJobActivities?$expand=jobActivity&$filter=(jobOrderId eq ${orderId})`,
		);
	},

	getAllowedWorkers(orderId: number): Promise<FetchResponse<ODataResponse<JobOrderAllowedWorker>>> {
		return api.get(
			`/odata/v1/JobOrderAllowedWorkers?$expand=worker($expand=user)&$filter=(jobOrderId eq ${orderId})`,
		);
	},

	getAllowedGroups(orderId: number): Promise<FetchResponse<ODataResponse<JobOrderAllowedGroup>>> {
		return api.get(
			`/odata/v1/JobOrderAllowedGroups?$expand=group&$filter=(jobOrderId eq ${orderId})`,
		);
	},

	getAllowedManagers(orderId: number): Promise<FetchResponse<ODataResponse<JobOrderManager>>> {
		return api.get(
			`/odata/v1/JobOrderManagers?$expand=worker($expand=user)&$filter=(jobOrderId eq ${orderId})`,
		);
	},

	getOrderRequirements(
		orderId: number,
	): Promise<FetchResponse<ODataResponse<JobOrderRequirement>>> {
		return api.get(
			`/odata/v1/JobOrderRequirements?$filter=(jobOrderId eq ${orderId})&$orderby=code`,
		);
	},

	getHoursPerActivityPerUserChartData(
		orderId: number,
		approved: boolean,
	): Promise<FetchResponse<HoursPerActivityPerUserChartData>> {
		return api.get(
			`/api/v1/JobOrders/${orderId}/actions/getHoursPerActivityPerUserChartData?approved=${approved}`,
		);
	},

	getHoursPerActivityChartData(
		orderId: number,
		approved: boolean,
	): Promise<FetchResponse<HoursPerActivityChartData>> {
		return api.get(
			`/api/v1/JobOrders/${orderId}/actions/getHoursPerActivityChartData?approved=${approved}`,
		);
	},

	getRequirements(
		orderId: number | null,
		searchString: string,
	): Promise<FetchResponse<RequirementAutocomplete[]>> {
		return api.get(
			`/api/v1/JobOrders/actions/getRequirements?orderId=${orderId === null || orderId === 0 ? 0 : orderId}&searchText=${searchString}`,
		);
	},

	generateExcel(query: string): Promise<FetchResponse<Blob>> {
		return api.get(`/api/v1/JobOrders/actions/generateExcel/${query}`, {
			responseType: 'blob',
		});
	},

	uploadOrders(form: FormData): Promise<FetchResponse<Blob>> {
		return api.post('/api/v1/JobOrders/actions/upload', form, {
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			responseType: 'blob',
		});
	},
};

export default orderEndpoints;
